import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook to fetch user subscription data.
 *
 * Handles authentication token retrieval (login-as mode or regular login)
 * and provides consistent subscription fetching across all pages.
 *
 * Usage:
 *   const { subscription, loading, error, refetch } = useSubscription();
 *
 * @returns {Object} { subscription, loading, error, refetch }
 */
const useSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const getAuthToken = () => {
    // Prioritize login-as token (admin viewing as user), then regular token
    return sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
  };

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      const loginAsToken = sessionStorage.getItem('loginAsToken');

      // Debug logging to verify correct token is being used
      console.log('[useSubscription] Fetching with token:', {
        hasLoginAsToken: !!loginAsToken,
        tokenSource: loginAsToken ? 'sessionStorage.loginAsToken' : 'localStorage.token',
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      if (!token) {
        setLoading(false);
        return;
      }

      // IMPORTANT: Use axios.create() with a fresh instance to ensure our token takes precedence
      // The global axios.defaults can override per-request headers in some scenarios
      const instance = axios.create({
        baseURL: API_BASE_URL,
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const response = await instance.get('/api/user/subscription/');

      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.response?.data?.detail || 'Failed to fetch subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  return {
    subscription,
    subscriptionLoading: loading,
    subscriptionError: error,
    refetchSubscription: fetchSubscription
  };
};

export default useSubscription;
